"use client"

import { useEffect, useState } from "react"
import { Container, Row, Col, Card, CardBody, CardHeader, Badge, Alert, Accordion } from "react-bootstrap"
import { toast } from '../../../common/Toast/toast';
import { getBatches } from "../../../../../services/BatchService"
import { fetchAllTrainers } from "../../../../../services/TrainerService"
import { getClassrooms } from "../../../../../services/ClassroomService.js"

const TrainerBatches = () => {
  const [trainersWithBatches, setTrainersWithBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [totalStats, setTotalStats] = useState({
    totalTrainers: 0,
    totalBatches: 0,
    totalStudents: 0,
    totalClassrooms: 0,
  })

  const fetchAllData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch all data in parallel
      const [batchesResponse, trainersResponse, classroomsResponse] = await Promise.all([
        getBatches(50000000, 1, ""),
        fetchAllTrainers(""),
        getClassrooms(50000000, 1, ""),
      ])

      const batches = batchesResponse.data
      const trainers = trainersResponse
      const classrooms = classroomsResponse.data

      // Create a map of classroom IDs to classroom data for quick lookup
      const classroomMap = {}
      classrooms.forEach((classroom) => {
        classroomMap[classroom.id] = classroom
      })

      // Group batches by trainer
      const trainerBatchMap = {}

      // Initialize all trainers with empty batch arrays
      trainers.forEach((trainer) => {
        trainerBatchMap[trainer.id] = {
          trainer: trainer,
          batches: [],
        }
      })

      // Process batches and assign to trainers
      batches.forEach((batch) => {
        if (Array.isArray(batch.trainerId) && batch.trainerId.length > 0) {
          batch.trainerId.forEach((trainer) => {
            if (trainerBatchMap[trainer.id]) {
              // Format batch data
              const formattedBatch = {
                ...batch,
                courseName: Array.isArray(batch.courseId) && batch.courseId.length > 0 ? batch.courseId[0].name : "N/A",
                courseCode: Array.isArray(batch.courseId) && batch.courseId.length > 0 ? batch.courseId[0].code : "N/A",
                classroomData: Array.isArray(batch.classroom) && batch.classroom.length > 0 ? batch.classroom[0] : null,
                studentCount: Array.isArray(batch.userId) ? batch.userId.length : 0,
                formattedStartTime: new Date(batch.startTime).toLocaleString(),
                formattedEndTime: new Date(batch.endTime).toLocaleString(),
                startDate: new Date(batch.startTime).toLocaleDateString(),
                endDate: new Date(batch.endTime).toLocaleDateString(),
                startTimeOnly: new Date(batch.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                endTimeOnly: new Date(batch.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              }

              trainerBatchMap[trainer.id].batches.push(formattedBatch)
            }
          })
        }
      })

      // Convert to array and sort by trainer name
      const trainersWithBatchesArray = Object.values(trainerBatchMap).sort((a, b) =>
        (a.trainer.trainerId || "").localeCompare(b.trainer.trainerId || ""),
      )

      setTrainersWithBatches(trainersWithBatchesArray)

      // Calculate total stats
      const totalBatches = batches.length
      const totalStudents = batches.reduce((total, batch) => {
        return total + (Array.isArray(batch.userId) ? batch.userId.length : 0)
      }, 0)
      const uniqueClassrooms = new Set()
      batches.forEach((batch) => {
        if (Array.isArray(batch.classroom) && batch.classroom.length > 0) {
          batch.classroom.forEach((classroom) => uniqueClassrooms.add(classroom.id))
        }
      })

      setTotalStats({
        totalTrainers: trainers.length,
        totalBatches: totalBatches,
        totalStudents: totalStudents,
        totalClassrooms: uniqueClassrooms.size,
      })
    } catch (err) {
      console.error("Error fetching data:", err)
      setError("Failed to fetch trainer batches data")
      toast.error("Failed to fetch trainer batches data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllData()
  }, [])

  const getModeBadge = (mode) => {
    const modeConfig = {
      Online: "success",
      Offline: "warning",
      Hybrid: "info",
    }
    return (
      <Badge bg={modeConfig[mode] || "secondary"} className="px-2 py-1">
        {mode}
      </Badge>
    )
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      Available: "success",
      Occupied: "danger",
      Maintenance: "warning",
      Reserved: "info",
    }
    return (
      <Badge bg={statusConfig[status] || "secondary"} className="px-2 py-1">
        {status}
      </Badge>
    )
  }

  if (loading) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
          <div className="spinner-border text-warning" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    )
  }

  if (error) {
    return (
      <Container fluid>
        <Alert variant="danger" className="text-center">
          <i className="fa fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      </Container>
    )
  }

  return (
    <Container fluid>      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Card>
            <CardHeader className="bg-warning text-white">
              <h4 className="mb-0 text-white">
                <i className="fa fa-users me-2"></i>
                All Trainer Batches Overview
              </h4>
            </CardHeader>
          </Card>
        </Col>
      </Row>

      {/* Overall Statistics */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center border-warning">
            <CardBody>
              <h3 className="text-warning">{totalStats.totalTrainers}</h3>
              <p className="mb-0">Total Trainers</p>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-success">
            <CardBody>
              <h3 className="text-success">{totalStats.totalBatches}</h3>
              <p className="mb-0">Total Batches</p>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-info">
            <CardBody>
              <h3 className="text-info">{totalStats.totalStudents}</h3>
              <p className="mb-0">Total Students</p>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-warning">
            <CardBody>
              <h3 className="text-warning">{totalStats.totalClassrooms}</h3>
              <p className="mb-0">Classrooms Used</p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Trainers and their Batches */}
      <Row>
        <Col>
          <Accordion defaultActiveKey="0">
            {trainersWithBatches.map((trainerData, index) => (
              <Accordion.Item eventKey={index.toString()} key={trainerData.trainer.id}>
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                    <div>
                      <strong>{trainerData.trainer.trainerId || "Unknown Trainer"}</strong>
                      <span className="text-muted ms-2">
                        - {trainerData.trainer.specialization || "No specialization"}
                      </span>
                    </div>
                    <div className="d-flex gap-2">
                      <Badge bg="warning">{trainerData.batches.length} Batches</Badge>
                      <Badge bg="success">
                        {trainerData.batches.reduce((total, batch) => total + batch.studentCount, 0)} Students
                      </Badge>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {trainerData.batches.length === 0 ? (
                    <Alert variant="info" className="text-center">
                      <i className="fa fa-info-circle me-2"></i>
                      No batches assigned to this trainer.
                    </Alert>
                  ) : (
                    <Row>
                      {trainerData.batches.map((batch) => (
                        <Col lg={6} xl={4} key={batch.id} className="mb-3">
                          <Card className="h-100 shadow-sm border-start border-warning border-3">
                            <CardHeader>
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 text-black me-2">{batch.name}</h6>
                                {getModeBadge(batch.mode)}
                              </div>
                            </CardHeader>
                            <CardBody className="p-3">
                              {/* Course Information */}
                              <div className="mb-2">
                                <small className="text-muted d-block">
                                  <i className="fa fa-book me-1"></i>Course
                                </small>
                                <div className="fw-bold">{batch.courseName}</div>
                                <small className="text-muted">Code: {batch.courseCode}</small>
                              </div>

                              {/* Classroom Information */}
                              <div className="mb-2">
                                <small className="text-muted d-block">
                                  <i className="fa fa-door-open me-1"></i>Classroom
                                </small>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="fw-bold">
                                    {batch.classroomData ? batch.classroomData.classroomNo : "N/A"}
                                  </span>
                                  {batch.classroomData && getStatusBadge(batch.classroomData.status)}
                                </div>
                              </div>

                              {/* Date and Time Information */}
                              <div className="mb-2">
                                <small className="text-muted d-block">
                                  <i className="fa fa-calendar me-1"></i>Schedule
                                </small>
                                <div className="row">
                                  <div className="col-6">
                                    <div className="small fw-bold">{batch.startDate}</div>
                                    <small className="text-success">{batch.startTimeOnly}</small>
                                  </div>
                                  <div className="col-6">
                                    <div className="small fw-bold">{batch.endDate}</div>
                                    <small className="text-danger">{batch.endTimeOnly}</small>
                                  </div>
                                </div>
                              </div>

                              {/* Students Count */}
                              <div className="mb-2">
                                <small className="text-muted d-block">
                                  <i className="fa fa-users me-1"></i>Students
                                </small>
                                <span className="badge bg-warning">{batch.studentCount} Enrolled</span>
                              </div>

                              {/* Meeting Information */}
                              {batch.mode !== "Offline" && (
                                <div className="mb-2">
                                  <small className="text-muted d-block">
                                    <i className="fa fa-video me-1"></i>Meeting
                                  </small>
                                  <div className="small">
                                    <div>
                                      <strong>ID:</strong> {batch.meetingId || "N/A"}
                                    </div>
                                    {batch.meetingLink && (
                                      <div>
                                        <a
                                          href={batch.meetingLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-decoration-none small"
                                        >
                                          <i className="fa fa-external-link me-1"></i>Join Meeting
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </CardBody>
                            <div className="card-footer bg-light p-2">
                              <small className="text-muted">
                                <i className="fa fa-clock me-1"></i>
                                Created: {new Date(batch.createdAt).toLocaleDateString()}
                              </small>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Col>
      </Row>
    </Container>
  )
}

export default TrainerBatches
